import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiFetch } from '../auth/session';
import { StripeInvoicesTable, type InvoiceRow } from '../components/StripeInvoicesTable';
import { FiscalProfileCard } from '../components/FiscalProfileCard';

type BillingStatus = {
  store_id: number;
  locked: boolean;
  reason: string | null;
  has_stripe_customer?: boolean;
  billing_enforcement_mode?: string;
  account: {
    account_status: string;
    plan_id: number | null;
    subscription_expires_at: string | null;
    stripe_subscription_id: string | null;
  } | null;
};

type SmsOffer = {
  sms_credits_balance: number;
  packs: Array<{
    pack_code: string;
    label: string;
    credits: number;
    price_cents: number;
  }>;
  can_purchase_sms_packs: boolean;
  purchase_blocked_code?: string | null;
};

type ReservationsStatus = {
  enabled: boolean;
  unit_cost_cents: number;
  unit_cost_eur: string;
  usage: {
    total_count: number;
    unclaimed_count: number;
    unclaimed_amount_cents: number;
    period_count: number;
    period_amount_cents: number;
  };
};

function statusLabel(status: string | undefined) {
  const map: Record<string, string> = {
    TRIAL: 'Prova',
    ACTIVE: 'Attivo',
    PAST_DUE: 'In ritardo',
    SUSPENDED: 'Sospeso',
  };
  return map[String(status || '').toUpperCase()] || status || 'Nessuno';
}

function daysRemaining(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const exp = new Date(iso).getTime();
  if (!Number.isFinite(exp)) return null;
  return Math.ceil((exp - Date.now()) / (24 * 60 * 60 * 1000));
}

const SMS_LOW_BALANCE_THRESHOLD = 20;

export function TenantBillingPage() {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [smsOffer, setSmsOffer] = useState<SmsOffer | null>(null);
  const [smsPackCode, setSmsPackCode] = useState('sms_100');
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [reservations, setReservations] = useState<ReservationsStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    setError(null);
    const res = await apiFetch('/api/v1/tenant/billing/status');
    const body = (await res.json().catch(() => ({}))) as BillingStatus & {
      error?: string;
      message?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `status ${res.status}`);
    setStatus(body);

    const smsRes = await apiFetch('/api/v1/tenant/billing/sms-packs');
    if (smsRes.ok) {
      const smsBody = (await smsRes.json()) as SmsOffer;
      setSmsOffer(smsBody);
      if (smsBody.packs?.[0] && !smsBody.packs.some((p) => p.pack_code === smsPackCode)) {
        setSmsPackCode(smsBody.packs[0].pack_code);
      }
    }
    const invRes = await apiFetch('/api/v1/tenant/billing/invoices');
    if (invRes.ok) {
      const invBody = (await invRes.json().catch(() => ({}))) as { invoices?: InvoiceRow[] };
      setInvoices(Array.isArray(invBody.invoices) ? invBody.invoices : []);
    }

    const resRes = await apiFetch('/api/v1/tenant/billing/reservations');
    if (resRes.ok) {
      const resBody = (await resRes.json().catch(() => null)) as ReservationsStatus | null;
      if (resBody) setReservations(resBody);
    }
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  async function openPortal() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/tenant/billing/portal', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = (await res.json().catch(() => ({}))) as {
        url?: string;
        error?: string;
        message?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `portal ${res.status}`);
      if (!body.url) throw new Error('Portal URL mancante');
      window.location.assign(body.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function buySmsPack() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/tenant/billing/sms-pack-checkout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ packCode: smsPackCode }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        url?: string;
        offline_granted?: boolean;
        error?: string;
        message?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `sms pack ${res.status}`);
      if (body.offline_granted) {
        setToast('Crediti SMS aggiornati (modalità offline / smoke).');
        await load();
        setBusy(false);
        return;
      }
      if (!body.url) throw new Error('Checkout SMS URL mancante');
      window.location.assign(body.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function activateReservations() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/tenant/billing/reservations/activate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = (await res.json().catch(() => ({}))) as {
        setup_intent?: { setup_intent_id: string; client_secret: string | null } | null;
        enabled?: boolean;
        message?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `activate ${res.status}`);
      if (body.enabled) {
        setToast('Prenotazioni già attive.');
        await load();
        setBusy(false);
        return;
      }
      // If we have a setup intent, we need Stripe.js to confirm card
      // For now, show a message — the confirm endpoint handles the rest
      if (body.setup_intent?.setup_intent_id) {
        setToast('Setup intent creato. Conferma la carta per attivare le prenotazioni.');
        // In production, load Stripe.js and confirm card setup
        // For now, auto-confirm via the confirm endpoint (dev mode)
        const confirmRes = await apiFetch('/api/v1/tenant/billing/reservations/confirm', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ setup_intent_id: body.setup_intent.setup_intent_id }),
        });
        const confirmBody = (await confirmRes.json().catch(() => ({}))) as {
          ok?: boolean;
          enabled?: boolean;
          message?: string;
          error?: string;
        };
        if (!confirmRes.ok) throw new Error(confirmBody.message || confirmBody.error || `confirm ${confirmRes.status}`);
        setToast(confirmBody.message || 'Prenotazioni attivate.');
        await load();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const account = status?.account;
  const enforceMode = String(status?.billing_enforcement_mode || 'off');
  const remainingDays = daysRemaining(account?.subscription_expires_at);
  const smsLow =
    smsOffer != null &&
    Number.isFinite(smsOffer.sms_credits_balance) &&
    smsOffer.sms_credits_balance >= 0 &&
    smsOffer.sms_credits_balance < SMS_LOW_BALANCE_THRESHOLD;

  return (
    <section className="sk-saas-page sk-saas-page--dense">
      <article className="sk-saas-card">
        <h1 className="sk-saas-card__title">Fatturazione</h1>
        <p className="sk-saas-card__sub">
          Abbonamento store-scoped · Stripe Checkout · Portal · pacchetti SMS
        </p>
        <p className="sk-saas-hint" style={{ marginTop: '0.65rem' }}>
          Lo sblocco moduli avviene dopo il webhook Stripe (non dalla sola pagina di success). Gate
          server: <code>BILLING_ENFORCEMENT_MODE={enforceMode}</code>
          {enforceMode === 'off' ? ' (lock soft, moduli non bloccati da billing)' : ''}.
        </p>
      </article>

      <FiscalProfileCard />

      {toast ? <div className="sk-saas-banner">{toast}</div> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      {status?.locked ? (
        <div className="sk-saas-banner sk-saas-banner--danger">
          Account bloccato{status.reason ? `: ${status.reason}` : ''}. Completa il pagamento o apri
          il Customer Portal per ripristinare l’accesso.
        </div>
      ) : null}

      {!status?.locked &&
      remainingDays != null &&
      remainingDays >= 0 &&
      remainingDays <= 7 &&
      String(account?.account_status || '').toUpperCase() === 'TRIAL' ? (
        <div className="sk-saas-banner">
          Prova in scadenza: {remainingDays === 0 ? 'oggi' : `${remainingDays} giorno/i rimanenti`}.
          Attiva un piano per evitare il blocco.
        </div>
      ) : null}

      {smsLow ? (
        <div className="sk-saas-banner">
          Crediti SMS bassi ({smsOffer?.sms_credits_balance}). Acquista un pack per non interrompere
          gli OTP prenotazioni.
        </div>
      ) : null}

      <article className="sk-saas-card">
        <h2 className="sk-saas-card__title">Stato attuale</h2>
        {!status ? (
          <p className="sk-saas-hint">Caricamento…</p>
        ) : (
          <div className="sk-saas-kpi" style={{ marginTop: '0.85rem' }}>
            <div className="sk-saas-kpi__tile">
              <p className="sk-saas-kpi__label">Account</p>
              <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem' }}>
                {statusLabel(account?.account_status)}
              </p>
            </div>
            <div className="sk-saas-kpi__tile">
              <p className="sk-saas-kpi__label">Accesso</p>
              <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem' }}>
                {status.locked ? 'Bloccato' : 'Attivo'}
              </p>
            </div>
            <div className="sk-saas-kpi__tile">
              <p className="sk-saas-kpi__label">Scadenza</p>
              <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem' }}>
                {account?.subscription_expires_at
                  ? new Date(account.subscription_expires_at).toLocaleDateString('it-IT')
                  : '—'}
                {remainingDays != null
                  ? ` · ${remainingDays < 0 ? 'scaduto' : `${remainingDays}g`}`
                  : ''}
              </p>
            </div>
            <div className="sk-saas-kpi__tile">
              <p className="sk-saas-kpi__label">Customer Stripe</p>
              <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem' }}>
                {status.has_stripe_customer ? 'Collegato' : 'Assente'}
              </p>
            </div>
          </div>
        )}
      </article>

      <div className="sk-saas-split">
        <article className="sk-saas-card">
          <h2 className="sk-saas-card__title">Moduli attivi</h2>
          <p className="sk-saas-card__sub">
            Piano Free · acquista moduli dallo store
          </p>
          <div className="sk-saas-form" style={{ marginTop: '0.85rem' }}>
            <p className="sk-saas-hint">
              Il tuo piano è <strong>Free</strong> (gratis). Acquista i moduli che ti servono
              dallo store, uno alla volta. Ogni modulo ha il suo prezzo mensile.
            </p>
            <Link
              className="sk-btn sk-btn--block"
              to="/tenant/store"
            >
              Vai allo Store moduli
            </Link>
            <button
              type="button"
              className="sk-btn sk-btn--quiet sk-btn--block"
              disabled={busy || !status?.has_stripe_customer}
              onClick={() => void openPortal()}
              title={
                status?.has_stripe_customer
                  ? 'Apri Stripe Customer Portal (fatture e metodo di pagamento)'
                  : 'Disponibile dopo il primo checkout Stripe'
              }
            >
              Customer Portal Stripe
            </button>
          </div>
        </article>

        <article className="sk-saas-card">
          <h2 className="sk-saas-card__title">Prenotazioni</h2>
          <p className="sk-saas-card__sub">
            Servizio base · pay-per-reservation · fatturazione fine mese
          </p>
          <div className="sk-saas-form" style={{ marginTop: '0.85rem' }}>
            {reservations?.enabled ? (
              <>
                <div className="sk-saas-kpi" style={{ marginBottom: '0.75rem' }}>
                  <div className="sk-saas-kpi__tile">
                    <p className="sk-saas-kpi__label">Stato</p>
                    <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem', color: '#16a34a' }}>
                      Attivo
                    </p>
                  </div>
                  <div className="sk-saas-kpi__tile">
                    <p className="sk-saas-kpi__label">Costo/pren.</p>
                    <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem' }}>
                      €{reservations.unit_cost_eur}
                    </p>
                  </div>
                  <div className="sk-saas-kpi__tile">
                    <p className="sk-saas-kpi__label">Pren. mese</p>
                    <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem' }}>
                      {reservations.usage.period_count}
                    </p>
                  </div>
                  <div className="sk-saas-kpi__tile">
                    <p className="sk-saas-kpi__label">Totale mese</p>
                    <p className="sk-saas-kpi__value" style={{ fontSize: '1.05rem' }}>
                      €{(reservations.usage.period_amount_cents / 100).toFixed(2)}
                    </p>
                  </div>
                </div>
                <p className="sk-saas-hint">
                  Ogni prenotazione ricevuta costa €{reservations.unit_cost_eur}. A fine mese
                  viene fatturato il totale sulla carta registrata.
                </p>
              </>
            ) : (
              <>
                <p className="sk-saas-hint">
                  Prenotazioni è un servizio base della piattaforma. Ogni prenotazione ricevuta
                  ha un costo una tantum (default €1.50). A fine mese il totale viene addebitato
                  sulla carta. Attivazione gratuita, serve solo una carta su Stripe.
                </p>
                <button
                  type="button"
                  className="sk-btn sk-btn--block"
                  disabled={busy}
                  onClick={() => void activateReservations()}
                >
                  {busy ? 'Elaborazione…' : 'Attiva prenotazioni con carta'}
                </button>
              </>
            )}
          </div>
        </article>

        <article className="sk-saas-card">
          <h2 className="sk-saas-card__title">Pacchetti SMS</h2>
          <p className="sk-saas-card__sub">
            Crediti OTP prenotazioni
            {smsOffer ? ` · saldo ${smsOffer.sms_credits_balance}` : ''}
          </p>
          <div className="sk-saas-form" style={{ marginTop: '0.85rem' }}>
            <label className="sk-field">
              <span>Pack</span>
              <select
                className="sk-input"
                value={smsPackCode}
                disabled={busy || !smsOffer?.packs?.length}
                onChange={(e) => setSmsPackCode(e.target.value)}
              >
                {(smsOffer?.packs || []).map((p) => (
                  <option key={p.pack_code} value={p.pack_code}>
                    {p.label} · {p.credits} SMS · €{(p.price_cents / 100).toFixed(2)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="sk-btn sk-btn--block"
              disabled={busy || !smsOffer?.can_purchase_sms_packs}
              onClick={() => void buySmsPack()}
              title={
                smsOffer?.can_purchase_sms_packs
                  ? 'Acquista crediti SMS'
                  : smsOffer?.purchase_blocked_code || 'Acquisto non disponibile'
              }
            >
              {busy ? 'Elaborazione…' : 'Acquista pack SMS'}
            </button>
            {!smsOffer?.can_purchase_sms_packs ? (
              <p className="sk-saas-hint">
                Acquisto riservato a account TRIAL/ACTIVE
                {smsOffer?.purchase_blocked_code
                  ? ` (${smsOffer.purchase_blocked_code})`
                  : ''}.
              </p>
            ) : null}
          </div>
        </article>
      </div>

      <article className="sk-saas-card">
        <h2 className="sk-saas-card__title">Fatture Stripe</h2>
        <p className="sk-saas-card__sub">
          PDF / hosted invoice da webhook Stripe · Portal resta disponibile sopra
        </p>
        <div style={{ marginTop: '0.85rem' }}>
          <StripeInvoicesTable
            invoices={invoices}
            refreshBasePath="/api/v1/tenant/billing/invoices"
            onRefreshed={(inv) =>
              setInvoices((prev) =>
                prev.map((p) => (p.stripe_invoice_id === inv.stripe_invoice_id ? inv : p)),
              )
            }
          />
        </div>
      </article>
    </section>
  );
}

export function BillingBlockedBanner({
  locked,
  reason,
}: {
  locked: boolean;
  reason?: string | null;
}) {
  if (!locked) return null;
  return (
    <div className="sk-saas-banner sk-saas-banner--danger" style={{ marginBottom: '0.75rem' }}>
      Billing bloccato{reason ? `: ${reason}` : ''}.{' '}
      <Link to="/tenant/billing">Vai all’abbonamento</Link>
    </div>
  );
}

export function BillingSuccessPage() {
  const { refreshEntitlements } = useAuth();
  const [searchParams] = useSearchParams();
  const offline = searchParams.get('offline') === '1';
  const [pollNote, setPollNote] = useState('Verifica stato…');

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        await refreshEntitlements();
        const res = await apiFetch('/api/v1/tenant/billing/status');
        if (!res.ok) return;
        const body = (await res.json()) as { locked?: boolean; account?: { account_status?: string } };
        if (cancelled) return;
        if (!body.locked && body.account?.account_status) {
          setPollNote(`Account: ${statusLabel(body.account.account_status)}`);
          return;
        }
        setPollNote(
          offline
            ? 'Checkout offline (nessun pagamento reale). Controlla lo stato in abbonamento.'
            : `In attesa webhook… tentativo ${attempts}`,
        );
      } catch {
        if (!cancelled) setPollNote('Impossibile aggiornare ora — apri Fatturazione.');
      }
    };
    void tick();
    const id = window.setInterval(() => {
      if (attempts >= 8) {
        window.clearInterval(id);
        return;
      }
      void tick();
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshEntitlements, offline]);

  return (
    <section className="sk-saas-page">
      <article className="sk-saas-card">
        <h1 className="sk-saas-card__title">
          {offline ? 'Checkout offline' : 'Pagamento avviato'}
        </h1>
        <p className="sk-saas-card__sub">
          {offline
            ? 'Nessuna chiave Stripe: sessione placeholder. Non c’è addebito reale.'
            : 'Se hai completato Stripe Checkout, lo stato account si aggiorna via webhook.'}
        </p>
        <p className="sk-saas-hint" style={{ marginTop: '0.75rem' }}>
          {pollNote}
        </p>
        <div className="sk-saas-actions" style={{ marginTop: '1rem' }}>
          <Link className="sk-btn" to="/tenant/billing">
            Stato abbonamento
          </Link>
          <Link className="sk-btn sk-btn--quiet" to="/tenant/home">
            Home
          </Link>
        </div>
      </article>
    </section>
  );
}

export function BillingCancelPage() {
  return (
    <section className="sk-saas-page">
      <article className="sk-saas-card">
        <h1 className="sk-saas-card__title">Checkout annullato</h1>
        <p className="sk-saas-card__sub">Nessun addebito. Puoi riprovare quando vuoi.</p>
        <div className="sk-saas-actions" style={{ marginTop: '1rem' }}>
          <Link className="sk-btn" to="/tenant/billing">
            Riprova
          </Link>
          <Link className="sk-btn sk-btn--quiet" to="/tenant/home">
            Home
          </Link>
        </div>
      </article>
    </section>
  );
}

/** Legacy offline placeholder path → success. */
export function BillingReturnRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={`/billing/success${qs ? `?${qs}` : ''}`} replace />;
}
